import type { PublishedPrototypeVersion } from '@ideoz/core';

/**
 * What the Publish menu says about a published prototype (test findings 1 and 6).
 *
 * A prototype has one link. `<slug>` (the main link) serves one version, and every version ever
 * published under it also has a fixed link `<slug>/v<number>`. Publishing any version, newer or
 * older, moves the main link to it, which is how a founder rolls back or moves forward.
 *
 * The links open the build where Nest uploads it, on the demo domain (US 686):
 * `https://demo.ideoz.ai/<slug>/v<number>/`. They used to be the app's own `/p/<slug>/v<number>`
 * page, which looked the build up and showed it in an iframe; that page still answers links
 * shared before.
 */
export interface PublishMenuState {
  mainUrl: string;
  /** The version on screen is the one the main link serves. */
  activeIsLive: boolean;
  /** The number of the version the main link serves; null when not recorded. */
  liveNumber: number | null;
  /** The version on screen is not what the main link serves (hidden from the menu for now). */
  canUpdateToActive: boolean;
  /** Fixed links, oldest version first. */
  versionLinks: Array<{ number: number; url: string; isLive: boolean; isActive: boolean }>;
  /**
   * The link to share for the version on screen: its fixed link `<slug>/v<number>/`, or the
   * main link when that serves it and its number was not recorded. Null when the version on
   * screen is not published (or not known yet). The menu shows only this link.
   */
  activeUrl: string | null;
}

export function publishMenuState(args: {
  /** Where published builds are served, with its trailing slash: `ApiUrls.prototypeDemoLink`. */
  demoLink: string;
  slug: string;
  publishedVersionId: string | null;
  publishedVersions: readonly PublishedPrototypeVersion[];
  /** The version the workbench is showing; null when not known yet. */
  activeVersionId: string | null;
}): PublishMenuState {
  const { demoLink, slug, publishedVersionId, publishedVersions, activeVersionId } = args;
  // A folder URL ends with a slash: the build's assets are relative to it (`base: "./"`).
  const mainUrl = `${demoLink}${slug}/`;
  const activeIsLive = Boolean(activeVersionId && activeVersionId === publishedVersionId);
  const liveNumber = publishedVersions.find((v) => v.versionId === publishedVersionId)?.number ?? null;
  const versionLinks = [...publishedVersions]
    .sort((a, b) => a.number - b.number)
    .map((v) => ({
      number: v.number,
      // Versions published before they were renumbered keep the folder they were uploaded to.
      url: `${mainUrl}${v.folder ?? `v${v.number}`}/`,
      isLive: v.versionId === publishedVersionId,
      isActive: v.versionId === activeVersionId,
    }));
  const activeUrl =
    versionLinks.find((link) => link.isActive)?.url ?? (activeIsLive ? mainUrl : null);
  return {
    mainUrl,
    activeIsLive,
    liveNumber,
    // An unknown active version (versions not loaded) never offers it: publishing then would
    // not know which version it moves the link to.
    canUpdateToActive: Boolean(activeVersionId) && !activeIsLive,
    versionLinks,
    activeUrl,
  };
}
